import { useEffect, useRef } from 'react';
import { Country } from 'App/i18n/enums';
import { config } from 'config';
import useStoreInfo from './useStoreInfo';

const SATISMETER_URL = 'https://app.satismeter.com/js';
const BR_WRITE_KEY = config.satismeter.brWriteKey;

function loadSatismeter(): void {
  if (document.querySelector(`script[src="${SATISMETER_URL}"]`)) return;
  // Queue pattern: calls made before the script loads are replayed on load
  if (!(window as any).satismeter) {
    const fn = function (...args: unknown[]) {
      ((fn as any).q = (fn as any).q || []).push(args);
    };
    (fn as any).l = Date.now();
    (window as any).satismeter = fn;
  }
  const script = document.createElement('script');
  script.async = true;
  script.src = SATISMETER_URL;
  const ref = document.getElementsByTagName('script')[0];
  if (ref?.parentNode) {
    ref.parentNode.insertBefore(script, ref);
  } else {
    document.head.appendChild(script);
  }
}

export function useSatismeterBR(options?: {
  event?: string;
  trackOnMount?: boolean;
}): void {
  const { event, trackOnMount = false } = options ?? {};
  const { storeInfo } = useStoreInfo();
  const storeId = storeInfo?.id ?? '';
  const country = storeInfo?.country ?? '';
  const storeName = storeInfo?.name ?? '';
  const email = storeInfo?.user?.email;
  const trackedRef = useRef(new Set<string>());

  useEffect(() => {
    if (!BR_WRITE_KEY || country !== Country.BRAZIL || !storeId) return;
    loadSatismeter();
    window.satismeter({
      writeKey: BR_WRITE_KEY,
      userId: storeId,
      traits: { name: storeName, email },
    });
  }, [storeId, country, storeName, email]);

  useEffect(() => {
    if (!BR_WRITE_KEY || country !== Country.BRAZIL || !storeId) return;
    if (!event || !trackOnMount) return;
    // Traits can change mid-session (store info is refreshed from Redux), so
    // keep every tracked store+event pair to avoid duplicate survey
    // impressions, even if a previous store becomes active again
    const trackKey = `${storeId}:${event}`;
    if (trackedRef.current.has(trackKey)) return;
    trackedRef.current.add(trackKey);
    window.satismeter('track', { event });
  }, [storeId, country, event, trackOnMount]);
}
